import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  TextField,
  Button,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ShieldIcon from "@mui/icons-material/Shield";
import type { Plan, PaymentFormData } from "./types";
import axios from "axios";
import jwtDecode from "jwt-decode";

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  selectedPlan: Plan;
  billingCycle: "MONTHLY" | "YEARLY";
  restaurantName: string;
  onSubmit: (data: PaymentFormData) => void;
  selectedRestraunt: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  open,
  onClose,
  selectedPlan,
  billingCycle,
  restaurantName,
  onSubmit,
  selectedRestraunt,
}) => {
  const [form, setForm] = useState<PaymentFormData>({
    cardNumber: "",
    expiryDate: "",
    cvv: "",
    cardholderName: "",
  });
  const [errors, setErrors] = useState<
    Partial<Record<keyof PaymentFormData, string>>
  >({});
  const [loading, setLoading] = useState(false);
  const [checkoutId, setCheckOutId] = useState("");
  const [openPaymentDialog, setopenPaymentDialog] = useState(false);
  const returnUrl = `${window.location.origin}/confirmationpage`;

  const pricing = selectedPlan.pricing.find(
    (p) => p.displayName === billingCycle,
  );
  const wpwOptions = {
    billingAddress: {
      country: "US",
      state: "NY",
      city: "New York",
      postcode: "12345",
      street1: "Suite 1234",
      street2: "Some Road",
    },
    mandatoryBillingFields: {
      country: true,
      state: true,
      city: true,
      postcode: true,
      street1: true,
      street2: false,
    },
    forceCardHolderEqualsBillingName: true,
  };

  const formatCardNumber = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 16);
    return digits.replace(/(.{4})/g, "$1 ").trim();
  };

  const formatExpiry = (val: string) => {
    const digits = val.replace(/\D/g, "").slice(0, 4);
    if (digits.length > 2) return digits.slice(0, 2) + "/" + digits.slice(2);
    return digits;
  };

  const handleChange = (field: keyof PaymentFormData, value: string) => {
    let formatted = value;
    if (field === "cardNumber") formatted = formatCardNumber(value);
    if (field === "expiryDate") formatted = formatExpiry(value);
    if (field === "cvv") formatted = value.replace(/\D/g, "").slice(0, 4);
    setForm((prev) => ({ ...prev, [field]: formatted }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = (): boolean => {
    const e: typeof errors = {};
    const cardDigits = form.cardNumber.replace(/\s/g, "");
    if (cardDigits.length < 13 || cardDigits.length > 16)
      e.cardNumber = "Enter a valid card number";
    if (!/^\d{2}\/\d{2}$/.test(form.expiryDate))
      e.expiryDate = "Use MM/YY format";
    else {
      const [mm] = form.expiryDate.split("/").map(Number);
      if (mm < 1 || mm > 12) e.expiryDate = "Invalid month";
    }
    if (form.cvv.length < 3) e.cvv = "Enter a valid CVV";
    if (form.cardholderName.trim().length < 2)
      e.cardholderName = "Enter cardholder name";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setLoading(true);
    const userId = localStorage.getItem("gotasteadmintoken");
    const decoded = jwtDecode(userId);
    axios({
      method: "get",
      url: `${process.env.REACT_APP_SECRET}/api/GoTasteUser/GetGoTasteUserByIdForCheckout/${decoded.UserId}`,
    })
      .then(async (res) => {
        const paymentData = {
          email: res.data.email,
          amount: pricing.discountedPrice,
          returnUrl: returnUrl,
        };
        const paymentDetailsResponse = await axios.get(
          `${process.env.REACT_APP_SECRET}/api/Order/GetPaymentDetails?email=${paymentData.email}&amount=${paymentData.amount}&returnUrl=null`,
        );

        localStorage.setItem(
          "checkoutDetails",
          JSON.stringify(paymentDetailsResponse.data),
        );

        const checkout = {
          id: decoded.UserId,
          cycleId: pricing?.cycleId,
          planId: selectedPlan?.planId,
          restaurantId: selectedRestraunt,
          resultCode: paymentDetailsResponse.data?.result.code ?? "",
          resultDescription:
            paymentDetailsResponse.data?.result.description ?? "",
          buildNumber: paymentDetailsResponse.data.buildNumber ?? "",
          timestampUtc: new Date(paymentDetailsResponse.data.timestamp),
          ndc: paymentDetailsResponse.data.ndc ?? "",
          transactionId: paymentDetailsResponse.data.id ?? "",
          integrityHash: paymentDetailsResponse.data.integrity ?? "",
          createdAt: new Date().toISOString(),
        };

        localStorage.setItem("planDetails", JSON.stringify(checkout));

        // await axios.post(
        //   `${process.env.REACT_APP_SECRET}/api/Order/InsertCartPaymentAttempts`,
        //   checkout,
        // );

        setCheckOutId(paymentDetailsResponse.data.id);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const formatPrice = (n: number) =>
    new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n);

  useEffect(() => {
    if (checkoutId) {
      window.wpwlOptions = {
        // billingAddress: {
        //   country: "US",
        //   state: "NY",
        //   city: "New York",
        //   postcode: "12345",
        //   street1: "Suite 1234",
        //   street2: "Some Road",
        // },
        billingAddress: {},
        mandatoryBillingFields: {
          country: true,
          state: true,
          city: true,
          postcode: true,
          street1: true,
          street2: false,
        },
        forceCardHolderEqualsBillingName: true,
      };
      const paymentURL = process.env.REACT_APP_PAYMENT_WIDGET_URL;
      const script = document.createElement("script");
      script.src = paymentURL + "?checkoutId=" + checkoutId;
      script.async = true;
      script.crossOrigin = "anonymous";
      document.body.appendChild(script);
      setopenPaymentDialog(true);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [checkoutId]);

  const handleClose = () => {
    setopenPaymentDialog(false);
    setLoading(false);
    onSubmit(form);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ color: "#371b17" }}>
            Complete Payment
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {restaurantName}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 4 }}>
        {/* Order summary */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, rgba(55, 27, 23, 0.04) 0%, rgba(55, 27, 23, 0.08) 100%)",
            borderRadius: 3,
            p: 2.5,
            mb: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontSize: 12 }}
              >
                Plan
              </Typography>
              <Typography variant="h6" sx={{ color: "#371b17" }}>
                {selectedPlan.name}
              </Typography>
            </Box>
            <Chip
              label={billingCycle === "Monthly Plan" ? "Monthly" : "Yearly"}
              size="small"
              sx={{ bgcolor: "#371b17", color: "#fff", fontWeight: 600 }}
            />
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total
            </Typography>
            <Box sx={{ textAlign: "right" }}>
              {pricing && pricing.discountPct > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: "line-through",
                    color: "text.disabled",
                    fontSize: 12,
                  }}
                >
                  £{formatPrice(pricing.discountedPrice)}
                </Typography>
              )}
              <Typography
                variant="h5"
                sx={{ color: "#371b17", fontWeight: 800 }}
              >
                £{formatPrice(pricing?.discountedPrice ?? 0)}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "text.secondary", ml: 0.5 }}
                >
                  /{billingCycle === "Monthly Plan" ? "mo" : "yr"}
                </Typography>
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Payment form */}
        <Box component="form" onSubmit={handleSubmit}>
          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CreditCardIcon sx={{ color: "#371b17", fontSize: 20 }} />
            <Typography
              variant="subtitle2"
              sx={{ color: "#371b17", fontWeight: 700 }}
            >
              Card Details
            </Typography>
          </Box>

          <TextField
            label="Cardholder Name"
            fullWidth
            value={form.cardholderName}
            onChange={(e) => handleChange("cardholderName", e.target.value)}
            error={!!errors.cardholderName}
            helperText={errors.cardholderName}
            sx={{ mb: 2 }}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <TextField
            label="Card Number"
            fullWidth
            value={form.cardNumber}
            onChange={(e) => handleChange("cardNumber", e.target.value)}
            error={!!errors.cardNumber}
            helperText={errors.cardNumber}
            placeholder="1234 5678 9012 3456"
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <TextField
              label="Expiry Date"
              value={form.expiryDate}
              onChange={(e) => handleChange("expiryDate", e.target.value)}
              error={!!errors.expiryDate}
              helperText={errors.expiryDate}
              placeholder="MM/YY"
              sx={{ flex: 1 }}
            />
            <TextField
              label="CVV"
              value={form.cvv}
              onChange={(e) => handleChange("cvv", e.target.value)}
              error={!!errors.cvv}
              helperText={errors.cvv}
              placeholder="123"
              type="password"
              sx={{ flex: 1 }}
            />
          </Box> */}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <LockIcon />
              )
            }
            sx={{
              py: 1.8,
              fontSize: "1rem",
              background: "linear-gradient(135deg, #371b17 0%, #5a3a35 100%)",
              boxShadow: "0 8px 24px rgba(55, 27, 23, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #5a3a35 0%, #371b17 100%)",
                boxShadow: "0 12px 32px rgba(55, 27, 23, 0.4)",
              },
            }}
          >
            {loading
              ? "Processing..."
              : `Pay £${formatPrice(pricing?.discountedPrice ?? 0)}`}
          </Button>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              mt: 2.5,
            }}
          >
            <ShieldIcon sx={{ fontSize: 16, color: "text.disabled" }} />
            <Typography variant="caption" sx={{ color: "text.disabled" }}>
              Payments are secured with 256-bit SSL encryption
            </Typography>
          </Box>
        </Box>
        <Dialog
          open={openPaymentDialog}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            sx: { zIndex: 1400 },
          }}
        >
          <DialogContent>
            <form
              action={returnUrl}
              class="paymentWidgets"
              data-brands="VISA MASTER"
            ></form>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentModal;
